import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  Button,
  Input,
  Toggle,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { projectService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import {
  TabContent,
  FormSection,
  FormRow,
  FormActions,
  QuotaGrid,
} from "./styles";

type VolumeQuotasTabProps = {
  projectId: string;
  regionId: string;
};

const VolumeQuotasTab: FC<VolumeQuotasTabProps> = ({ projectId, regionId }) => {

  const [t] = useTranslation();
  const { viewAlert } = useUi();

  const [loading, setLoading] = useState<boolean>(true);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  const validationSchema = yup.object().shape({
    volumes: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    snapshots: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    backups: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    groups: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    per_volume_gigabytes: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    gigabytes: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    backup_gigabytes: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      volumes: -1,
      snapshots: -1,
      backups: -1,
      groups: -1,
      per_volume_gigabytes: -1,
      gigabytes: -1,
      backup_gigabytes: -1,
      force: false,
      set_for_all_regions: false,
    },
    onSubmit: (formValues) => {
      setUpdateLoading(true);

      const payload = {
        ...formValues,
        region_id: regionId,
      };

      projectService
        .setVolumeQuotas(projectId, payload)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("QUOTAS_UPDATED_SUCCESSFULLY"),
          });
          loadQuotas();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.detail ||
            extractErrorMessage(error?.response?.data) ||
            t("FAILED_TO_UPDATE_QUOTAS");
          viewAlert({
            severity: "error",
            message,
          });
        })
        .finally(() => {
          setUpdateLoading(false);
        });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
  } = formik;

  const loadQuotas = useCallback(() => {
    setLoading(true);
    projectService
      .getVolumeQuotas(projectId, regionId)
      .then((res) => {
        const quotas = res.data;
        setValues({
          volumes: quotas.volumes ?? -1,
          snapshots: quotas.snapshots ?? -1,
          backups: quotas.backups ?? -1,
          groups: quotas.groups ?? -1,
          per_volume_gigabytes: quotas.per_volume_gigabytes ?? -1,
          gigabytes: quotas.gigabytes ?? -1,
          backup_gigabytes: quotas.backup_gigabytes ?? -1,
          force: false,
          set_for_all_regions: false,
        });
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_QUOTAS");
        viewAlert({
          severity: "error",
          message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectId, regionId, setValues, t, viewAlert]);

  useEffect(() => {
    loadQuotas();
  }, [loadQuotas]);

  const onFormSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      formik.submitForm();
    },
    [formik]
  );

  const formikProps = {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
  };

  if (loading) {
    return (
      <TabContent>
        <p>{t("LOADING")}</p>
      </TabContent>
    );
  }

  return (
    <TabContent>
      <FormSection as="form" onSubmit={onFormSubmit}>
        <QuotaGrid>
          <FormRow>
            <Input
              required
              title={t("VOLUMES")}
              placeholder="-1"
              fieldName="volumes"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("SNAPSHOTS")}
              placeholder="-1"
              fieldName="snapshots"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("BACKUPS")}
              placeholder="-1"
              fieldName="backups"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("GROUPS")}
              placeholder="-1"
              fieldName="groups"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("PER_VOLUME_GIGABYTES")}
              placeholder="-1"
              fieldName="per_volume_gigabytes"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("GIGABYTES")}
              placeholder="-1"
              fieldName="gigabytes"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("BACKUP_GIGABYTES")}
              placeholder="-1"
              fieldName="backup_gigabytes"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>
        </QuotaGrid>

        <FormRow>
          <Toggle
            value={values.force}
            onChange={() => setFieldValue("force", !values.force)}
            title={t("FORCE_UPDATE")}
            toolTip={true}
            toolTipText={t("FORCE_UPDATE_HINT")}
          />
        </FormRow>

        <FormRow>
          <Toggle
            value={values.set_for_all_regions}
            onChange={() => setFieldValue("set_for_all_regions", !values.set_for_all_regions)}
            title={t("SET_FOR_ALL_REGIONS")}
            toolTip={true}
            toolTipText={t("SET_FOR_ALL_REGIONS_HINT")}
          />
        </FormRow>

        <FormActions>
          <Button
            type="submit"
            variant="primary"
            size="medium"
            title={t("UPDATE_QUOTAS")}
            disabled={updateLoading}
            loading={updateLoading}
          />
        </FormActions>
      </FormSection>
    </TabContent>
  );
};

export default VolumeQuotasTab;
